import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { UrlShortenerService } from '../../../services/url-shortener.service';

interface PricingPlan {
  name: string;
  serviceType: 'compute' | 'storage' | 'database'; // Add service type to differentiate between services
  ram?: string;
  vcpu?: number;
  storage?: string;
  diskType?: string;
  iops?: number;
  throughput?: string;
  dbEngine?: string;
  dbSize?: string;
  dbCpu?: number;
  dbRam?: string;
  discount: string;
  hourlyPrice: number;
  monthlyPrice: number;
  yearlyPrice: number;
  quantity?: number;  // Add optional quantity property to the interface
  minimumBilling?: number;
}

@Component({
  selector: 'app-pricing-calculator',
  templateUrl: './pricing-calculator.component.html',
  styleUrls: ['./pricing-calculator.component.scss']
})
export class PricingCalculatorComponent implements OnInit {
  // Calculator Properties
  calculator = {
    selectedServiceType: 'compute' as 'compute' | 'storage' | 'database', // Add service type selection
    selectedInstanceType: 'Shared CPU' as 'Shared CPU' | 'Dedicated CPU' | 'High Memory',
    selectedOS: 'linux' as 'linux' | 'windows',
    selectedBilling: 'monthly' as 'hourly' | 'monthly' | 'yearly',
    selectedPlan: '',
    quantity: 1  // Keep the default quantity for backward compatibility
  };

  selectedPlanDetails: PricingPlan | null = null;

  searchQuery: string = '';
  compareSelection: PricingPlan[] = [];
  showCompare: boolean = false;
  filterVcpuExact: number | null = null;
  filterRamExactGB: number | null = null;

  estimatedCost: number | null = null;
  estimatedMonthlyCost: number | null = null;
  estimatedYearlyCost: number | null = null;

  // All plans - these would normally be loaded from a service
  sharedCpuPlansLinux: PricingPlan[] = [
    {
      name: 'Insight Starter',
      serviceType: 'compute',
      ram: '4 GB',
      vcpu: 2,
      storage: '80 GB SSD',
      discount: '0%',
      hourlyPrice: 2.08,
      monthlyPrice: 1495,
      yearlyPrice: 14352,
      quantity: 1
    },
    {
      name: 'Insight Professional',
      serviceType: 'compute',
      ram: '8 GB',
      vcpu: 4,
      storage: '160 GB SSD',
      discount: '0%',
      hourlyPrice: 4.16,
      monthlyPrice: 2995,
      yearlyPrice: 28752,
      quantity: 1
    },
    {
      name: 'Insight Business',
      serviceType: 'compute',
      ram: '16 GB',
      vcpu: 6,
      storage: '320 GB SSD',
      discount: '0%',
      hourlyPrice: 6.38,
      monthlyPrice: 4595,
      yearlyPrice: 44112,
      quantity: 1
    },
    {
      name: 'Insight Enterprise',
      serviceType: 'compute',
      ram: '32 GB',
      vcpu: 8,
      storage: '480 GB SSD',
      discount: '0%',
      hourlyPrice: 10.41,
      monthlyPrice: 7495,
      yearlyPrice: 71952,
      quantity: 1
    },
    {
      name: 'Insight Elite',
      serviceType: 'compute',
      ram: '64 GB',
      vcpu: 12,
      storage: '640 GB SSD',
      discount: '0%',
      hourlyPrice: 18.05,
      monthlyPrice: 12995,
      yearlyPrice: 124752,
      quantity: 1
    },
    {
      name: 'Insight Premium',
      serviceType: 'compute',
      ram: '96 GB',
      vcpu: 24,
      storage: '840 GB SSD',
      discount: '0%',
      hourlyPrice: 34.72,
      monthlyPrice: 24995,
      yearlyPrice: 239952,
      quantity: 1
    },
    {
      name: 'Insight Advanced',
      serviceType: 'compute',
      ram: '128 GB',
      vcpu: 32,
      storage: '960 GB SSD',
      discount: '0%',
      hourlyPrice: 45.83,
      monthlyPrice: 32995,
      yearlyPrice: 316752,
      quantity: 1
    },
    {
      name: 'Insight Ultimate',
      serviceType: 'compute',
      ram: '192 GB',
      vcpu: 48,
      storage: '1280 GB SSD',
      discount: '0%',
      hourlyPrice: 63.88,
      monthlyPrice: 45995,
      yearlyPrice: 441552,
      quantity: 1
    }
  ];

  sharedCpuPlansWindows: PricingPlan[] = [
    {
      name: 'Insight Starter Windows',
      serviceType: 'compute',
      ram: '20 GB',
      vcpu: 2,
      storage: '200 GB SSD',
      discount: '15%',
      hourlyPrice: 6.73,
      monthlyPrice: 4846,
      yearlyPrice: 41201,
      quantity: 1,
      minimumBilling: 1000
    },
    {
      name: 'Insight Professional Windows',
      serviceType: 'compute',
      ram: '40 GB',
      vcpu: 4,
      storage: '400 GB SSD',
      discount: '15%',
      hourlyPrice: 10.1,
      monthlyPrice: 7272,
      yearlyPrice: 61812,
      quantity: 1,
      minimumBilling: 2000
    },
    // ... rest of the plans with serviceType property added
    // For brevity, I'll show just a few more examples
    {
      name: 'Insight Business Windows',
      serviceType: 'compute',
      ram: '60 GB',
      vcpu: 6,
      storage: '600 GB SSD',
      discount: '15%',
      hourlyPrice: 13.46,
      monthlyPrice: 9691,
      yearlyPrice: 82388,
      quantity: 1,
      minimumBilling: 3000
    },
    {
      name: 'Insight Enterprise Windows',
      serviceType: 'compute',
      ram: '80 GB',
      vcpu: 8,
      storage: '800 GB SSD',
      discount: '15%',
      hourlyPrice: 17.95,
      monthlyPrice: 12924,
      yearlyPrice: 109858,
      quantity: 1,
      minimumBilling: 4000
    },
    {
      name: 'Insight Elite Windows',
      serviceType: 'compute',
      ram: '100 GB',
      vcpu: 10,
      storage: '1000 GB SSD',
      discount: '15%',
      hourlyPrice: 21.93,
      monthlyPrice: 15780,
      yearlyPrice: 134130,
      quantity: 1,
      minimumBilling: 5000
    },
    {
      name: 'Insight Premium Windows',
      serviceType: 'compute',
      ram: '120 GB',
      vcpu: 12,
      storage: '1200 GB SSD',
      discount: '15%',
      hourlyPrice: 26.93,
      monthlyPrice: 19380,
      yearlyPrice: 164730,
      quantity: 1,
      minimumBilling: 6000
    },
    {
      name: 'Insight Advanced Windows',
      serviceType: 'compute',
      ram: '140 GB',
      vcpu: 14,
      storage: '1400 GB SSD',
      discount: '15%',
      hourlyPrice: 30.5,
      monthlyPrice: 21950,
      yearlyPrice: 186575,
      quantity: 1,
      minimumBilling: 7000
    },
    {
      name: 'Insight Ultimate Windows',
      serviceType: 'compute',
      ram: '160 GB',
      vcpu: 16,
      storage: '1600 GB SSD',
      discount: '15%',
      hourlyPrice: 35.9,
      monthlyPrice: 25840,
      yearlyPrice: 219640,
      quantity: 1,
      minimumBilling: 8000
    },
  ];

  dedicatedCpuPlansLinux: PricingPlan[] = [
    {
      name: 'Insight Power Starter',
      serviceType: 'compute',
      ram: '4 GB',
      vcpu: 2,
      storage: '80 GB SSD',
      discount: '0%',
      hourlyPrice: 3.19,
      monthlyPrice: 2295,
      yearlyPrice: 22032,
      quantity: 1
    },
    {
      name: 'Insight Power Pro',
      serviceType: 'compute',
      ram: '8 GB',
      vcpu: 4,
      storage: '160 GB SSD',
      discount: '0%',
      hourlyPrice: 6.38,
      monthlyPrice: 4595,
      yearlyPrice: 44112,
      quantity: 1
    },
    {
      name: 'Insight Power Business',
      serviceType: 'compute',
      ram: '32 GB',
      vcpu: 8,
      storage: '480 GB SSD',
      discount: '0%',
      hourlyPrice: 18.05,
      monthlyPrice: 12995,
      yearlyPrice: 124752,
      quantity: 1
    },
    {
      name: 'Insight Power Enterprise',
      serviceType: 'compute',
      ram: '64 GB',
      vcpu: 16,
      storage: '960 GB SSD',
      discount: '0%',
      hourlyPrice: 34.72,
      monthlyPrice: 24995,
      yearlyPrice: 239952,
      quantity: 1
    },
    {
      name: 'Insight Power Elite',
      serviceType: 'compute',
      ram: '96 GB',
      vcpu: 32,
      storage: '1280 GB SSD',
      discount: '0%',
      hourlyPrice: 62.63,
      monthlyPrice: 45095,
      yearlyPrice: 432912,
      quantity: 1
    },
    {
      name: 'Insight Power Ultimate',
      serviceType: 'compute',
      ram: '128 GB',
      vcpu: 48,
      storage: '1980 GB SSD',
      discount: '0%',
      hourlyPrice: 90.41,
      monthlyPrice: 65095,
      yearlyPrice: 624912,
      quantity: 1
    }
  ];

  dedicatedCpuPlansWindows: PricingPlan[] = [
    {
      name: 'Insight Power Starter Windows',
      serviceType: 'compute',
      ram: '20 GB',
      vcpu: 2,
      storage: '200 GB SSD',
      discount: '10%',
      hourlyPrice: 14,
      monthlyPrice: 10080,
      yearlyPrice: 90720,
      quantity: 1,
      minimumBilling: 4500
    },
    {
      name: 'Insight Power Pro Windows',
      serviceType: 'compute',
      ram: '40 GB',
      vcpu: 4,
      storage: '400 GB SSD',
      discount: '10%',
      hourlyPrice: 26,
      monthlyPrice: 18720,
      yearlyPrice: 168480,
      quantity: 1,
      minimumBilling: 6500
    },
    // ... rest of the plans with serviceType property added
    {
      name: 'Insight Power Business Windows',
      serviceType: 'compute',
      ram: '80 GB',
      vcpu: 8,
      storage: '800 GB SSD',
      discount: '10%',
      hourlyPrice: 52,
      monthlyPrice: 37440,
      yearlyPrice: 336960,
      quantity: 1,
      minimumBilling: 10400
    },
    {
      name: 'Insight Power Enterprise Windows',
      serviceType: 'compute',
      ram: '160 GB',
      vcpu: 16,
      storage: '1600 GB SSD',
      discount: '10%',
      hourlyPrice: 106,
      monthlyPrice: 76320,
      yearlyPrice: 686880,
      quantity: 1,
      minimumBilling: 20800
    },
    {
      name: 'Insight Power Elite Windows',
      serviceType: 'compute',
      ram: '320 GB',
      vcpu: 32,
      storage: '3200 GB SSD',
      discount: '10%',
      hourlyPrice: 209,
      monthlyPrice: 150480,
      yearlyPrice: 1354320,
      quantity: 1,
      minimumBilling: 41600
    },
    {
      name: 'Insight Power Ultimate Windows',
      serviceType: 'compute',
      ram: '480 GB',
      vcpu: 48,
      storage: '4800 GB SSD',
      discount: '10%',
      hourlyPrice: 314,
      monthlyPrice: 226080,
      yearlyPrice: 2034720,
      quantity: 1,
      minimumBilling: 62400
    },
  ];

  highMemoryPlansLinux: PricingPlan[] = [
    {
      name: 'Insight RAM Starter',
      serviceType: 'compute',
      ram: '8 GB',
      vcpu: 1,
      storage: '120 GB SSD',
      discount: '0%',
      hourlyPrice: 2.22,
      monthlyPrice: 1595,
      yearlyPrice: 15312,
      quantity: 1
    },
    {
      name: 'Insight RAM Pro',
      serviceType: 'compute',
      ram: '16 GB',
      vcpu: 2,
      storage: '160 GB SSD',
      discount: '0%',
      hourlyPrice: 3.96,
      monthlyPrice: 2850,
      yearlyPrice: 27360,
      quantity: 1
    },
    {
      name: 'Insight RAM Business',
      serviceType: 'compute',
      ram: '32 GB',
      vcpu: 4,
      storage: '320 GB SSD',
      discount: '0%',
      hourlyPrice: 7.92,
      monthlyPrice: 5700,
      yearlyPrice: 54720,
      quantity: 1
    },
    {
      name: 'Insight RAM Enterprise',
      serviceType: 'compute',
      ram: '64 GB',
      vcpu: 8,
      storage: '640 GB SSD',
      discount: '0%',
      hourlyPrice: 15.83,
      monthlyPrice: 11400,
      yearlyPrice: 109440,
      quantity: 1
    },
    {
      name: 'Insight RAM Elite',
      serviceType: 'compute',
      ram: '64 GB',
      vcpu: 8,
      storage: '960 GB SSD',
      discount: '0%',
      hourlyPrice: 17.61,
      monthlyPrice: 12680,
      yearlyPrice: 121728,
      quantity: 1
    },
    {
      name: 'Insight RAM Ultimate',
      serviceType: 'compute',
      ram: '128 GB',
      vcpu: 16,
      storage: '1295 GB SSD',
      discount: '0%',
      hourlyPrice: 40.27,
      monthlyPrice: 28995,
      yearlyPrice: 278352,
      quantity: 1
    }
  ];

  highMemoryPlansWindows: PricingPlan[] = [
    {
      name: 'Insight RAM Starter Windows',
      serviceType: 'compute',
      ram: '24 GB',
      vcpu: 2,
      storage: '200 GB SSD',
      discount: '10%',
      hourlyPrice: 18,
      monthlyPrice: 12960,
      yearlyPrice: 116640,
      quantity: 1,
      minimumBilling: 5500
    },
    {
      name: 'Insight RAM Pro Windows',
      serviceType: 'compute',
      ram: '48 GB',
      vcpu: 4,
      storage: '400 GB SSD',
      discount: '10%',
      hourlyPrice: 30,
      monthlyPrice: 21600,
      yearlyPrice: 194400,
      quantity: 1,
      minimumBilling: 8500
    },
    // ... rest of the plans with serviceType property added
    {
      name: 'Insight RAM Business Windows',
      serviceType: 'compute',
      ram: '72 GB',
      vcpu: 6,
      storage: '600 GB SSD',
      discount: '10%',
      hourlyPrice: 42,
      monthlyPrice: 30240,
      yearlyPrice: 272160,
      quantity: 1,
      minimumBilling: 11500
    },
    {
      name: 'Insight RAM Enterprise Windows',
      serviceType: 'compute',
      ram: '96 GB',
      vcpu: 8,
      storage: '800 GB SSD',
      discount: '10%',
      hourlyPrice: 54,
      monthlyPrice: 38880,
      yearlyPrice: 349920,
      quantity: 1,
      minimumBilling: 14500
    },
    {
      name: 'Insight RAM Elite Windows',
      serviceType: 'compute',
      ram: '120 GB',
      vcpu: 10,
      storage: '1000 GB SSD',
      discount: '10%',
      hourlyPrice: 66,
      monthlyPrice: 47520,
      yearlyPrice: 427680,
      quantity: 1,
      minimumBilling: 17500
    },
    {
      name: 'Insight RAM Ultimate Windows',
      serviceType: 'compute',
      ram: '144 GB',
      vcpu: 12,
      storage: '1200 GB SSD',
      discount: '10%',
      hourlyPrice: 78,
      monthlyPrice: 56160,
      yearlyPrice: 505440,
      quantity: 1,
      minimumBilling: 20500
    },
  ];

  // Storage Plans
  storagePlans: PricingPlan[] = [
    // Block Storage Plans
    {
      name: 'Block Storage - Basic',
      serviceType: 'storage',
      storage: '100 GB',
      diskType: 'SSD',
      iops: 3000,
      throughput: '300 Mbps',
      discount: '0%',
      hourlyPrice: 0.07, // ₹5 per GB per month / (24 * 30) hours
      monthlyPrice: 500, // 100 GB * ₹5 per GB = ₹500
      yearlyPrice: 6000,
      quantity: 1
    },
    {
      name: 'Block Storage - Standard',
      serviceType: 'storage',
      storage: '500 GB',
      diskType: 'SSD',
      iops: 3000,
      throughput: '300 Mbps',
      discount: '0%',
      hourlyPrice: 0.35, // ₹5 per GB per month / (24 * 30) hours
      monthlyPrice: 2500, // 500 GB * ₹5 per GB = ₹2500
      yearlyPrice: 30000,
      quantity: 1
    },
    {
      name: 'Block Storage - Premium',
      serviceType: 'storage',
      storage: '1 TB',
      diskType: 'NVMe SSD',
      iops: 10000,
      throughput: '1 Gbps',
      discount: '0%',
      hourlyPrice: 0.70, // ₹5 per GB per month / (24 * 30) hours
      monthlyPrice: 5000, // 1000 GB * ₹5 per GB = ₹5000
      yearlyPrice: 60000,
      quantity: 1
    },
    {
      name: 'Block Storage - Enterprise',
      serviceType: 'storage',
      storage: '5 TB',
      diskType: 'NVMe SSD',
      iops: 50000,
      throughput: '4 Gbps',
      discount: '0%',
      hourlyPrice: 3.47, // ₹5 per GB per month / (24 * 30) hours
      monthlyPrice: 25000, // 5000 GB * ₹5 per GB = ₹25000
      yearlyPrice: 300000,
      quantity: 1
    },
    // Object Storage Plans
    {
      name: 'Object Storage - Tier 1',
      serviceType: 'storage',
      storage: '100 GB - 1 TB',
      diskType: 'Object',
      iops: 1000,
      throughput: '100 GB',
      discount: '0%',
      hourlyPrice: 0.0005, // ₹4 per GB per month / (24 * 30) hours
      monthlyPrice: 400, // ₹4 per GB for 100 GB = ₹400
      yearlyPrice: 4800,
      quantity: 1
    },
    {
      name: 'Object Storage - Tier 2',
      serviceType: 'storage',
      storage: '1 TB - 5 TB',
      diskType: 'Object',
      iops: 1500,
      throughput: '500 GB',
      discount: '0%',
      hourlyPrice: 0.0004, // ₹3.5 per GB per month / (24 * 30) hours
      monthlyPrice: 3500, // ₹3.5 per GB for 1000 GB = ₹3500
      yearlyPrice: 42000,
      quantity: 1
    },
    {
      name: 'Object Storage - Tier 3',
      serviceType: 'storage',
      storage: '5 TB - 10 TB',
      diskType: 'Object',
      iops: 2000,
      throughput: '2 TB',
      discount: '0%',
      hourlyPrice: 0.0003, // ₹3 per GB per month / (24 * 30) hours
      monthlyPrice: 15000, // ₹3 per GB for 5000 GB = ₹15000
      yearlyPrice: 180000,
      quantity: 1
    },
    {
      name: 'Object Storage - Tier 4',
      serviceType: 'storage',
      storage: '10 TB+',
      diskType: 'Object',
      iops: 2500,
      throughput: '5 TB',
      discount: '0%',
      hourlyPrice: 0.0002, // ₹2.5 per GB per month / (24 * 30) hours
      monthlyPrice: 25000, // ₹2.5 per GB for 10000 GB = ₹25000
      yearlyPrice: 300000,
      quantity: 1
    },
    // Archive Storage Plans
    {
      name: 'Archive Storage - Basic',
      serviceType: 'storage',
      storage: '100 GB',
      diskType: 'Archive',
      iops: 100,
      throughput: '50 Mbps',
      discount: '0%',
      hourlyPrice: 0.0003, // ₹3 per GB per month / (24 * 30) hours
      monthlyPrice: 300, // ₹3 per GB for 100 GB = ₹300
      yearlyPrice: 3600,
      quantity: 1
    },
    {
      name: 'Archive Storage - Standard',
      serviceType: 'storage',
      storage: '500 GB',
      diskType: 'Archive',
      iops: 200,
      throughput: '100 Mbps',
      discount: '0%',
      hourlyPrice: 0.0015, // ₹3 per GB per month / (24 * 30) hours
      monthlyPrice: 1500, // ₹3 per GB for 500 GB = ₹1500
      yearlyPrice: 18000,
      quantity: 1
    },
    {
      name: 'Archive Storage - Enterprise',
      serviceType: 'storage',
      storage: '1 TB',
      diskType: 'Archive',
      iops: 500,
      throughput: '200 Mbps',
      discount: '0%',
      hourlyPrice: 0.003, // ₹3 per GB per month / (24 * 30) hours
      monthlyPrice: 3000, // ₹3 per GB for 1000 GB = ₹3000
      yearlyPrice: 36000,
      quantity: 1
    }
  ];

  // Database Plans - Based on Utho pricing model
  databasePlans: PricingPlan[] = [
    // MongoDB Shared CPU Plans
    {
      name: 'MongoDB Shared CPU Basic',
      serviceType: 'database',
      dbEngine: 'MongoDB',
      dbSize: 'Small',
      dbCpu: 2,
      dbRam: '4 GB',
      storage: '80 GB SSD',
      discount: '0%',
      hourlyPrice: 2.08,
      monthlyPrice: 1495,
      yearlyPrice: 14352,
      quantity: 1
    },
    {
      name: 'MongoDB Shared CPU Standard',
      serviceType: 'database',
      dbEngine: 'MongoDB',
      dbSize: 'Medium',
      dbCpu: 4,
      dbRam: '8 GB',
      storage: '160 GB SSD',
      discount: '0%',
      hourlyPrice: 4.16,
      monthlyPrice: 2995,
      yearlyPrice: 28752,
      quantity: 1
    },
    // MongoDB Dedicated CPU Plans
    {
      name: 'MongoDB Dedicated CPU Basic',
      serviceType: 'database',
      dbEngine: 'MongoDB',
      dbSize: 'Small',
      dbCpu: 2,
      dbRam: '4 GB',
      storage: '80 GB SSD',
      discount: '0%',
      hourlyPrice: 3.19,
      monthlyPrice: 2295,
      yearlyPrice: 22032,
      quantity: 1
    },
    {
      name: 'MongoDB Dedicated CPU Standard',
      serviceType: 'database',
      dbEngine: 'MongoDB',
      dbSize: 'Medium',
      dbCpu: 4,
      dbRam: '8 GB',
      storage: '160 GB SSD',
      discount: '0%',
      hourlyPrice: 6.38,
      monthlyPrice: 4595,
      yearlyPrice: 44112,
      quantity: 1
    },
    // MongoDB High Memory Plans
    {
      name: 'MongoDB High Memory Basic',
      serviceType: 'database',
      dbEngine: 'MongoDB',
      dbSize: 'Small',
      dbCpu: 1,
      dbRam: '8 GB',
      storage: '120 GB SSD',
      discount: '0%',
      hourlyPrice: 2.22,
      monthlyPrice: 1595,
      yearlyPrice: 15312,
      quantity: 1
    },
    {
      name: 'MongoDB High Memory Standard',
      serviceType: 'database',
      dbEngine: 'MongoDB',
      dbSize: 'Medium',
      dbCpu: 2,
      dbRam: '16 GB',
      storage: '160 GB SSD',
      discount: '0%',
      hourlyPrice: 3.96,
      monthlyPrice: 2850,
      yearlyPrice: 27360,
      quantity: 1
    },
    // MySQL Shared CPU Plans
    {
      name: 'MySQL Shared CPU Basic',
      serviceType: 'database',
      dbEngine: 'MySQL',
      dbSize: 'Small',
      dbCpu: 2,
      dbRam: '4 GB',
      storage: '80 GB SSD',
      discount: '0%',
      hourlyPrice: 2.08,
      monthlyPrice: 1495,
      yearlyPrice: 14352,
      quantity: 1
    },
    {
      name: 'MySQL Shared CPU Standard',
      serviceType: 'database',
      dbEngine: 'MySQL',
      dbSize: 'Medium',
      dbCpu: 4,
      dbRam: '8 GB',
      storage: '160 GB SSD',
      discount: '0%',
      hourlyPrice: 4.16,
      monthlyPrice: 2995,
      yearlyPrice: 28752,
      quantity: 1
    },
    // MySQL Dedicated CPU Plans
    {
      name: 'MySQL Dedicated CPU Basic',
      serviceType: 'database',
      dbEngine: 'MySQL',
      dbSize: 'Small',
      dbCpu: 2,
      dbRam: '4 GB',
      storage: '80 GB SSD',
      discount: '0%',
      hourlyPrice: 3.19,
      monthlyPrice: 2295,
      yearlyPrice: 22032,
      quantity: 1
    },
    {
      name: 'MySQL Dedicated CPU Standard',
      serviceType: 'database',
      dbEngine: 'MySQL',
      dbSize: 'Medium',
      dbCpu: 4,
      dbRam: '8 GB',
      storage: '160 GB SSD',
      discount: '0%',
      hourlyPrice: 6.38,
      monthlyPrice: 4595,
      yearlyPrice: 44112,
      quantity: 1
    },
    // MySQL High Memory Plans
    {
      name: 'MySQL High Memory Basic',
      serviceType: 'database',
      dbEngine: 'MySQL',
      dbSize: 'Small',
      dbCpu: 1,
      dbRam: '8 GB',
      storage: '120 GB SSD',
      discount: '0%',
      hourlyPrice: 2.22,
      monthlyPrice: 1595,
      yearlyPrice: 15312,
      quantity: 1
    },
    {
      name: 'MySQL High Memory Standard',
      serviceType: 'database',
      dbEngine: 'MySQL',
      dbSize: 'Medium',
      dbCpu: 2,
      dbRam: '16 GB',
      storage: '160 GB SSD',
      discount: '0%',
      hourlyPrice: 3.96,
      monthlyPrice: 2850,
      yearlyPrice: 27360,
      quantity: 1
    },
    // PostgreSQL Shared CPU Plans
    {
      name: 'PostgreSQL Shared CPU Basic',
      serviceType: 'database',
      dbEngine: 'PostgreSQL',
      dbSize: 'Small',
      dbCpu: 2,
      dbRam: '4 GB',
      storage: '80 GB SSD',
      discount: '0%',
      hourlyPrice: 2.08,
      monthlyPrice: 1495,
      yearlyPrice: 14352,
      quantity: 1
    },
    {
      name: 'PostgreSQL Shared CPU Standard',
      serviceType: 'database',
      dbEngine: 'PostgreSQL',
      dbSize: 'Medium',
      dbCpu: 4,
      dbRam: '8 GB',
      storage: '160 GB SSD',
      discount: '0%',
      hourlyPrice: 4.16,
      monthlyPrice: 2995,
      yearlyPrice: 28752,
      quantity: 1
    },
    // PostgreSQL Dedicated CPU Plans
    {
      name: 'PostgreSQL Dedicated CPU Basic',
      serviceType: 'database',
      dbEngine: 'PostgreSQL',
      dbSize: 'Small',
      dbCpu: 2,
      dbRam: '4 GB',
      storage: '80 GB SSD',
      discount: '0%',
      hourlyPrice: 3.19,
      monthlyPrice: 2295,
      yearlyPrice: 22032,
      quantity: 1
    },
    {
      name: 'PostgreSQL Dedicated CPU Standard',
      serviceType: 'database',
      dbEngine: 'PostgreSQL',
      dbSize: 'Medium',
      dbCpu: 4,
      dbRam: '8 GB',
      storage: '160 GB SSD',
      discount: '0%',
      hourlyPrice: 6.38,
      monthlyPrice: 4595,
      yearlyPrice: 44112,
      quantity: 1
    },
    // PostgreSQL High Memory Plans
    {
      name: 'PostgreSQL High Memory Basic',
      serviceType: 'database',
      dbEngine: 'PostgreSQL',
      dbSize: 'Small',
      dbCpu: 1,
      dbRam: '8 GB',
      storage: '120 GB SSD',
      discount: '0%',
      hourlyPrice: 2.22,
      monthlyPrice: 1595,
      yearlyPrice: 15312,
      quantity: 1
    },
    {
      name: 'PostgreSQL High Memory Standard',
      serviceType: 'database',
      dbEngine: 'PostgreSQL',
      dbSize: 'Medium',
      dbCpu: 2,
      dbRam: '16 GB',
      storage: '160 GB SSD',
      discount: '0%',
      hourlyPrice: 3.96,
      monthlyPrice: 2850,
      yearlyPrice: 27360,
      quantity: 1
    }
  ];

  constructor(private cdr: ChangeDetectorRef, private urlShortenerService: UrlShortenerService) { }

  ngOnInit(): void {
    // Load estimate from URL if present
    this.loadEstimateFromUrl();
    
    // Initialize calculator with default values if no estimate was loaded
    if (this.sharedCpuPlansLinux && this.sharedCpuPlansLinux.length > 0 && !this.selectedPlans.length) {
      this.calculator.selectedPlan = this.sharedCpuPlansLinux[0].name;
    }
    // Update plan details based on initial selection
    this.updatePlanDetails();
    // Ensure the selected plan is properly set after updating details
    if (this.selectedPlanDetails) {
      this.calculator.selectedPlan = this.selectedPlanDetails.name;
    }
    this.calculateEstimate();
    
    // If an estimate was loaded from URL, recalculate total estimates
    if (this.selectedPlans.length > 0) {
      this.calculateTotalEstimate();
    }
  }

  updateCalculatorPlan(planName: string): void {
    this.calculator.selectedPlan = planName;
    this.updatePlanDetails();
    this.calculateEstimate();
    this.cdr.detectChanges();
  }

  updateCalculatorOS(os: 'linux' | 'windows'): void {
    this.calculator.selectedOS = os;
    // When OS changes, update the plan list and potentially reset the selected plan
    this.updatePlanDetails();
    // Ensure the selected plan is valid for the new OS
    if (this.selectedPlanDetails) {
      this.calculator.selectedPlan = this.selectedPlanDetails.name;
    }
    this.calculateEstimate();
    this.cdr.detectChanges();
  }

  updateCalculatorInstanceType(type: 'Shared CPU' | 'Dedicated CPU' | 'High Memory'): void {
    this.calculator.selectedInstanceType = type;
    // When instance type changes, update the plan list and potentially reset the selected plan
    this.updatePlanDetails();
    // Ensure the selected plan is valid for the new instance type
    if (this.selectedPlanDetails) {
      this.calculator.selectedPlan = this.selectedPlanDetails.name;
    }
    this.calculateEstimate();
    this.cdr.detectChanges();
  }

  // New method to update service type
  updateServiceType(type: 'compute' | 'storage' | 'database'): void {
    this.calculator.selectedServiceType = type;
    // When service type changes, update the plan list and potentially reset the selected plan
    this.updatePlanDetails();
    // Ensure the selected plan is valid for the new service type
    if (this.selectedPlanDetails) {
      this.calculator.selectedPlan = this.selectedPlanDetails.name;
    }
    this.calculateEstimate();
    this.cdr.detectChanges();
  }

  updateCalculatorBilling(billing: 'hourly' | 'monthly' | 'yearly'): void {
    this.calculator.selectedBilling = billing;
    this.calculateEstimate();
    this.cdr.detectChanges();
  }

  updateCalculatorQuantity(quantity: number): void {
    this.calculator.quantity = quantity;
    this.calculateEstimate();
    this.cdr.detectChanges();
  }
  
  // New method to update quantity for a specific plan
  updatePlanQuantity(planName: string, quantity: number): void {
    // Find the plan in the appropriate array and update its quantity
    let planArray: PricingPlan[] = [];
    
    if (this.calculator.selectedServiceType === 'compute') {
      if (this.calculator.selectedInstanceType === 'Shared CPU') {
        planArray = this.calculator.selectedOS === 'linux' ? this.sharedCpuPlansLinux : this.sharedCpuPlansWindows;
      } else if (this.calculator.selectedInstanceType === 'Dedicated CPU') {
        planArray = this.calculator.selectedOS === 'linux' ? this.dedicatedCpuPlansLinux : this.dedicatedCpuPlansWindows;
      } else if (this.calculator.selectedInstanceType === 'High Memory') {
        planArray = this.calculator.selectedOS === 'linux' ? this.highMemoryPlansLinux : this.highMemoryPlansWindows;
      }
    } else if (this.calculator.selectedServiceType === 'storage') {
      planArray = this.storagePlans;
    } else if (this.calculator.selectedServiceType === 'database') {
      planArray = this.databasePlans;
    }
    
    const plan = planArray.find(p => p.name === planName);
    if (plan) {
      plan.quantity = Math.max(1, quantity); // Ensure quantity is at least 1
    }
    
    this.cdr.detectChanges();
  }
  
  // New method to select a plan and add it to the estimate
  selectAndAddPlan(plan: PricingPlan): void {
    // Set the selected plan in the calculator
    this.calculator.selectedPlan = plan.name;
    // Update the selected plan details
    this.selectedPlanDetails = plan;
    // Add the plan to the estimate with its specific quantity
    this.addPlanToEstimate();
  }

  private updatePlanDetails(): void {
    let planArray: PricingPlan[] = [];

    if (this.calculator.selectedServiceType === 'compute') {
      if (this.calculator.selectedInstanceType === 'Shared CPU') {
        planArray = this.calculator.selectedOS === 'linux' ? this.sharedCpuPlansLinux : this.sharedCpuPlansWindows;
      } else if (this.calculator.selectedInstanceType === 'Dedicated CPU') {
        planArray = this.calculator.selectedOS === 'linux' ? this.dedicatedCpuPlansLinux : this.dedicatedCpuPlansWindows;
      } else if (this.calculator.selectedInstanceType === 'High Memory') {
        planArray = this.calculator.selectedOS === 'linux' ? this.highMemoryPlansLinux : this.highMemoryPlansWindows;
      }
    } else if (this.calculator.selectedServiceType === 'storage') {
      planArray = this.storagePlans;
    } else if (this.calculator.selectedServiceType === 'database') {
      planArray = this.databasePlans;
    }

    // Find the selected plan in the appropriate array
    const foundPlan = planArray.find(plan => plan.name === this.calculator.selectedPlan);
    
    if (foundPlan) {
      // If the selected plan exists in the array, update details
      this.selectedPlanDetails = foundPlan;
    } else if (planArray.length > 0) {
      // If the selected plan doesn't exist, default to the first plan in the array
      this.calculator.selectedPlan = planArray[0].name;
      this.selectedPlanDetails = planArray[0];
    } else {
      // If no plans are available, clear the selection
      this.selectedPlanDetails = null;
    }
  }

  calculateEstimate(): void {
    if (this.selectedPlanDetails) {
      let baseRate = 0;

      switch (this.calculator.selectedBilling) {
        case 'hourly':
          baseRate = this.selectedPlanDetails.hourlyPrice * this.calculator.quantity;
          this.estimatedCost = parseFloat(baseRate.toFixed(2));
          this.estimatedMonthlyCost = parseFloat((baseRate * 24 * 30).toFixed(2));
          this.estimatedYearlyCost = parseFloat((this.estimatedMonthlyCost * 12).toFixed(2));
          break;
        case 'monthly':
          baseRate = this.selectedPlanDetails.monthlyPrice * this.calculator.quantity;
          this.estimatedCost = parseFloat(baseRate.toFixed(2));
          this.estimatedMonthlyCost = parseFloat(baseRate.toFixed(2));
          this.estimatedYearlyCost = parseFloat((baseRate * 12).toFixed(2));
          break;
        case 'yearly':
          baseRate = this.selectedPlanDetails.yearlyPrice * this.calculator.quantity;
          this.estimatedCost = parseFloat(baseRate.toFixed(2));
          this.estimatedMonthlyCost = parseFloat((baseRate / 12).toFixed(2));
          this.estimatedYearlyCost = parseFloat(baseRate.toFixed(2));
          break;
      }
    } else {
      this.estimatedCost = null;
      this.estimatedMonthlyCost = null;
      this.estimatedYearlyCost = null;
    }
    this.cdr.detectChanges();
  }

  // Export functionality methods
  exportToPDF(): void {
 if (this.selectedPlans.length === 0) {
      alert('Please add plans to your estimate before exporting.');
      return;
    }

    const content = this.generatePDFContent();
    const printWindow = window.open('', '_blank');
    
    if (printWindow) {
      printWindow.document.write(content);
      printWindow.document.close();
      
      // Wait for content to load then print
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };
    }
  }

  private generatePDFContent(): string {
    const currentDate = new Date().toLocaleDateString('en-IN');
    
    // Generate grouped plans HTML
    let plansHtml = '';
    
    // Compute Services
    const computePlans = this.getSelectedPlansByServiceType('compute');
    if (computePlans.length > 0) {
      plansHtml += `
        <tr>
          <td colspan="4" style="padding: 12px; background-color: #dbeafe; font-weight: bold; color: #1e40af; border-bottom: 2px solid #93c5fd;">
            <i class="bx bx-server"></i> Compute Services (${computePlans.length} ${computePlans.length === 1 ? 'Plan' : 'Plans'})
          </td>
        </tr>
      `;
      computePlans.forEach(item => {
        const instanceType = this.getInstanceTypeFromPlanName(item.plan.name);
        const minBilling = item.plan.minimumBilling || 0;
        const totalMinBilling = minBilling * item.quantity;
        plansHtml += `
          <tr>
            <td style="padding: 12px; border-bottom: 1px solid #e5e7eb;">
              ${item.plan.name}
              <br><small style="color: #6b7280;">${item.plan.vcpu} vCPU, ${item.plan.ram} RAM, ${item.plan.storage}</small>
              ${minBilling > 0 ? `<br><small style="color: #dc2626; font-weight: 600; background-color: #fef2f2; padding: 4px 8px; border-radius: 4px; display: inline-block; margin-top: 4px;">⚠️ ${instanceType} - Minimum Billing: ₹${totalMinBilling}</small>` : ''}
            </td>
            <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: center;">${item.quantity}</td>
            <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: right;">₹${item.plan.monthlyPrice.toFixed(2)}</td>
            <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: right; font-weight: bold;">₹${(item.plan.monthlyPrice * item.quantity).toFixed(2)}</td>
          </tr>
        `;
      });
    }
    
    // Storage Services
    const storagePlans = this.getSelectedPlansByServiceType('storage');
    if (storagePlans.length > 0) {
      plansHtml += `
        <tr>
          <td colspan="4" style="padding: 12px; background-color: #d1fae5; font-weight: bold; color: #065f46; border-bottom: 2px solid #6ee7b7;">
            <i class="bx bx-data"></i> Storage Services (${storagePlans.length} ${storagePlans.length === 1 ? 'Plan' : 'Plans'})
          </td>
        </tr>
      `;
      storagePlans.forEach(item => {
        plansHtml += `
          <tr>
            <td style="padding: 12px; border-bottom: 1px solid #e5e7eb;">${item.plan.name}<br><small style="color: #6b7280;">${item.plan.storage}, ${item.plan.diskType}, ${item.plan.iops} IOPS</small></td>
            <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: center;">${item.quantity}</td>
            <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: right;">₹${item.plan.monthlyPrice.toFixed(2)}</td>
            <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: right; font-weight: bold;">₹${(item.plan.monthlyPrice * item.quantity).toFixed(2)}</td>
          </tr>
        `;
      });
    }
    
    // Database Services
    const databasePlans = this.getSelectedPlansByServiceType('database');
    if (databasePlans.length > 0) {
      plansHtml += `
        <tr>
          <td colspan="4" style="padding: 12px; background-color: #e9d5ff; font-weight: bold; color: #581c87; border-bottom: 2px solid #c084fc;">
            <i class="bx bx-cylinder"></i> Database Services (${databasePlans.length} ${databasePlans.length === 1 ? 'Plan' : 'Plans'})
          </td>
        </tr>
      `;
      databasePlans.forEach(item => {
        plansHtml += `
          <tr>
            <td style="padding: 12px; border-bottom: 1px solid #e5e7eb;">${item.plan.name}<br><small style="color: #6b7280;">${item.plan.dbEngine}, ${item.plan.dbCpu} vCPU, ${item.plan.dbRam} RAM</small></td>
            <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: center;">${item.quantity}</td>
            <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: right;">₹${item.plan.monthlyPrice.toFixed(2)}</td>
            <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: right; font-weight: bold;">₹${(item.plan.monthlyPrice * item.quantity).toFixed(2)}</td>
          </tr>
        `;
      });
    }

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Cloud Pricing Estimate - Insightful IT Services</title>
        <style>
          @media print {
            body { margin: 0; }
            @page { margin: 1cm; }
          }
          body {
            font-family: Arial, sans-serif;
            padding: 40px;
            max-width: 800px;
            margin: 0 auto;
          }
          .header {
            text-align: center;
            margin-bottom: 40px;
            border-bottom: 3px solid #2563eb;
            padding-bottom: 20px;
          }
          .company-name {
            font-size: 28px;
            font-weight: bold;
            color: #1e40af;
            margin-bottom: 5px;
          }
          .document-title {
            font-size: 20px;
            color: #374151;
          }
          .info-section {
            margin-bottom: 30px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 30px;
          }
          th {
            background-color: #f3f4f6;
            padding: 12px;
            text-align: left;
            font-weight: bold;
            border-bottom: 2px solid #d1d5db;
          }
          .summary {
            background-color: #eff6ff;
            padding: 20px;
            border-radius: 8px;
            margin-top: 20px;
          }
          .summary-row {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            border-bottom: 1px solid #dbeafe;
          }
          .summary-row:last-child {
            border-bottom: none;
            font-weight: bold;
            font-size: 18px;
            color: #1e40af;
          }
          .footer {
            margin-top: 40px;
            text-align: center;
            color: #6b7280;
            font-size: 12px;
            border-top: 1px solid #e5e7eb;
            padding-top: 20px;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-name">Insightful IT Services LLP</div>
          <div class="document-title">Cloud Infrastructure Pricing Estimate</div>
          <div style="font-size: 14px; color: #6b7280; margin-top: 10px;">Date: ${currentDate}</div>
        </div>

        <div class="info-section">
          <p><strong>Billing Cycle:</strong> ${this.calculator.selectedBilling.charAt(0).toUpperCase() + this.calculator.selectedBilling.slice(1)}</p>
        </div>

        <table>
          <thead>
            <tr>
              <th>Plan Name</th>
              <th style="text-align: center;">Quantity</th>
              <th style="text-align: right;">Unit Price (Monthly)</th>
              <th style="text-align: right;">Total Price</th>
            </tr>
          </thead>
          <tbody>
            ${plansHtml}
          </tbody>
        </table>

        <div class="summary">
          <div class="summary-row">
            <span>Estimated Hourly Cost:</span>
            <span>₹${this.totalEstimateCost.toFixed(2)}/hr</span>
          </div>
          <div class="summary-row">
            <span>Estimated Monthly Cost:</span>
            <span>₹${this.totalMonthlyCost.toFixed(2)}/mo</span>
          </div>
          <div class="summary-row">
            <span>Estimated Yearly Cost:</span>
            <span>₹${this.totalYearlyCost.toFixed(2)}/yr</span>
          </div>
        </div>

        <div class="footer">
          <p><strong>Insightful IT Services LLP</strong></p>
          <p>610, Synergy Premises, Kanchpada, Malad (West), Mumbai - 400064</p>
          <p>Phone: +91 9167663638 | Email: info@insightfulitservices.com</p>
          <p style="margin-top: 10px;">This is an automated estimate. Final pricing may vary based on actual usage and additional services.</p>
        </div>
      </body>
      </html>
    `;
  }

  exportToExcel(): void {
    if (this.selectedPlans.length === 0) {
      alert('Please add plans to your estimate before exporting.');
      return;
    }

    // Create CSV content with proper encoding
    const currentDate = new Date().toLocaleDateString('en-IN');
    let csvContent = 'Insightful IT Services LLP - Cloud Pricing Estimate\n';
    csvContent += `Date: ${currentDate}\n`;
    csvContent += `Billing Cycle: ${this.calculator.selectedBilling.charAt(0).toUpperCase() + this.calculator.selectedBilling.slice(1)}\n\n`;
        
    // Compute Services
    const computePlans = this.getSelectedPlansByServiceType('compute');
    if (computePlans.length > 0) {
      csvContent += `\nCOMPUTE SERVICES (${computePlans.length} ${computePlans.length === 1 ? 'Plan' : 'Plans'})\n`;
      csvContent += 'Plan Name,Configuration,Instance Type,Minimum Billing,Quantity,Unit Price (Monthly),Total Price\n';
      computePlans.forEach(item => {
        const config = `${item.plan.vcpu} vCPU ${item.plan.ram} RAM ${item.plan.storage}`;
        const instanceType = this.getInstanceTypeFromPlanName(item.plan.name);
        const minBilling = item.plan.minimumBilling || 0;
        const totalMinBilling = minBilling * item.quantity;
        // Use INR symbol properly encoded
        csvContent += `"${item.plan.name}","${config}","${instanceType}","INR ${totalMinBilling}",${item.quantity},INR ${item.plan.monthlyPrice.toFixed(2)},INR ${(item.plan.monthlyPrice * item.quantity).toFixed(2)}\n`;
      });
    }
        
    // Storage Services
    const storagePlans = this.getSelectedPlansByServiceType('storage');
    if (storagePlans.length > 0) {
      csvContent += `\nSTORAGE SERVICES (${storagePlans.length} ${storagePlans.length === 1 ? 'Plan' : 'Plans'})\n`;
      csvContent += 'Plan Name,Configuration,Quantity,Unit Price (Monthly),Total Price\n';
      storagePlans.forEach(item => {
        const config = `${item.plan.storage} ${item.plan.diskType} ${item.plan.iops} IOPS`;
        csvContent += `"${item.plan.name}","${config}",${item.quantity},INR ${item.plan.monthlyPrice.toFixed(2)},INR ${(item.plan.monthlyPrice * item.quantity).toFixed(2)}\n`;
      });
    }
        
    // Database Services
    const databasePlans = this.getSelectedPlansByServiceType('database');
    if (databasePlans.length > 0) {
      csvContent += `\nDATABASE SERVICES (${databasePlans.length} ${databasePlans.length === 1 ? 'Plan' : 'Plans'})\n`;
      csvContent += 'Plan Name,Configuration,Quantity,Unit Price (Monthly),Total Price\n';
      databasePlans.forEach(item => {
        const config = `${item.plan.dbEngine} ${item.plan.dbCpu} vCPU ${item.plan.dbRam} RAM`;
        csvContent += `"${item.plan.name}","${config}",${item.quantity},INR ${item.plan.monthlyPrice.toFixed(2)},INR ${(item.plan.monthlyPrice * item.quantity).toFixed(2)}\n`;
      });
    }
        
    // Summary
    csvContent += `\nSUMMARY\n`;
    csvContent += `Estimated Hourly Cost,INR ${this.totalEstimateCost.toFixed(2)}/hr\n`;
    csvContent += `Estimated Monthly Cost,INR ${this.totalMonthlyCost.toFixed(2)}/mo\n`;
    csvContent += `Estimated Yearly Cost,INR ${this.totalYearlyCost.toFixed(2)}/yr\n`;
        
    csvContent += `\nCONTACT INFORMATION\n`;
    csvContent += 'Insightful IT Services LLP\n';
    csvContent += '610 Synergy Premises Kanchpada Malad (West) Mumbai - 400064\n';
    csvContent += 'Phone: +91 9167663638\n';
    csvContent += 'Email: info@insightfulitservices.com\n';
        
    // Create blob and download with UTF-8 encoding
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
        
    link.setAttribute('href', url);
    link.setAttribute('download', `Cloud_Pricing_Estimate_${currentDate.replace(/\//g, '-')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  printEstimate(): void {
    if (this.selectedPlans.length === 0) {
      alert('Please add plans to your estimate before printing.');
      return;
    }
    
    const content = this.generatePrintContent();
    const printWindow = window.open('', '_blank');
    
    if (printWindow) {
      printWindow.document.write(content);
      printWindow.document.close();
      
      // Wait for content to load then print
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };
    }
  }
  
  private generatePrintContent(): string {
    const currentDate = new Date().toLocaleDateString('en-IN');
    
    // Generate grouped plans HTML
    let plansHtml = '';
    
    // Compute Services
    const computePlans = this.getSelectedPlansByServiceType('compute');
    if (computePlans.length > 0) {
      plansHtml += `
        <tr>
          <td colspan="6" style="padding: 12px; background-color: #dbeafe; font-weight: bold; color: #1e40af; border-bottom: 2px solid #93c5fd; text-align: left;">
            <i class="bx bx-server" style="margin-right: 8px;"></i> COMPUTE SERVICES (${computePlans.length} ${computePlans.length === 1 ? 'Plan' : 'Plans'})
          </td>
        </tr>
        <tr style="background-color: #f9fafb; font-weight: bold;">
          <th style="padding: 12px; border-bottom: 2px solid #e5e7eb; text-align: left;">Plan Name</th>
          <th style="padding: 12px; border-bottom: 2px solid #e5e7eb; text-align: left;">Configuration</th>
          <th style="padding: 12px; border-bottom: 2px solid #e5e7eb; text-align: left;">Instance Type</th>
          <th style="padding: 12px; border-bottom: 2px solid #e5e7eb; text-align: right;">Minimum Billing</th>
          <th style="padding: 12px; border-bottom: 2px solid #e5e7eb; text-align: center;">Quantity</th>
          <th style="padding: 12px; border-bottom: 2px solid #e5e7eb; text-align: right;">Total Price</th>
        </tr>
      `;
      computePlans.forEach(item => {
        const instanceType = this.getInstanceTypeFromPlanName(item.plan.name);
        const minBilling = item.plan.minimumBilling || 0;
        const totalMinBilling = minBilling * item.quantity;
        plansHtml += `
          <tr>
            <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">${item.plan.name}</td>
            <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">${item.plan.vcpu} vCPU, ${item.plan.ram} RAM, ${item.plan.storage}</td>
            <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">${instanceType}</td>
            <td style="padding: 10px; border-bottom: 1px solid #e5e7eb; text-align: right; color: ${minBilling > 0 ? '#dc2626; font-weight: bold;' : '#6b7280'};">
              ${minBilling > 0 ? 'INR ' + totalMinBilling : 'Not Applicable'}
            </td>
            <td style="padding: 10px; border-bottom: 1px solid #e5e7eb; text-align: center;">${item.quantity}</td>
            <td style="padding: 10px; border-bottom: 1px solid #e5e7eb; text-align: right; font-weight: bold;">
              INR ${(this.calculator.selectedBilling === 'hourly' ? item.plan.hourlyPrice * item.quantity : 
                   this.calculator.selectedBilling === 'monthly' ? item.plan.monthlyPrice * item.quantity : 
                   item.plan.yearlyPrice * item.quantity).toFixed(2)}
            </td>
          </tr>
        `;
      });
    }
    
    // Storage Services
    const storagePlans = this.getSelectedPlansByServiceType('storage');
    if (storagePlans.length > 0) {
      plansHtml += `
        <tr>
          <td colspan="6" style="padding: 12px; background-color: #d1fae5; font-weight: bold; color: #065f46; border-bottom: 2px solid #6ee7b7; text-align: left;">
            <i class="bx bx-data" style="margin-right: 8px;"></i> STORAGE SERVICES (${storagePlans.length} ${storagePlans.length === 1 ? 'Plan' : 'Plans'})
          </td>
        </tr>
        <tr style="background-color: #f9fafb; font-weight: bold;">
          <th style="padding: 12px; border-bottom: 2px solid #e5e7eb; text-align: left;">Plan Name</th>
          <th style="padding: 12px; border-bottom: 2px solid #e5e7eb; text-align: left;">Configuration</th>
          <th style="padding: 12px; border-bottom: 2px solid #e5e7eb; text-align: center;">Quantity</th>
          <th style="padding: 12px; border-bottom: 2px solid #e5e7eb; text-align: right;">Unit Price</th>
          <th style="padding: 12px; border-bottom: 2px solid #e5e7eb; text-align: right;" colspan="2">Total Price</th>
        </tr>
      `;
      storagePlans.forEach(item => {
        plansHtml += `
          <tr>
            <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">${item.plan.name}</td>
            <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">${item.plan.storage}, ${item.plan.diskType}, ${item.plan.iops} IOPS</td>
            <td style="padding: 10px; border-bottom: 1px solid #e5e7eb; text-align: center;">${item.quantity}</td>
            <td style="padding: 10px; border-bottom: 1px solid #e5e7eb; text-align: right;">
              INR ${(this.calculator.selectedBilling === 'hourly' ? item.plan.hourlyPrice : 
                   this.calculator.selectedBilling === 'monthly' ? item.plan.monthlyPrice : 
                   item.plan.yearlyPrice).toFixed(2)}
            </td>
            <td style="padding: 10px; border-bottom: 1px solid #e5e7eb; text-align: right; font-weight: bold;" colspan="2">
              INR ${(this.calculator.selectedBilling === 'hourly' ? item.plan.hourlyPrice * item.quantity : 
                   this.calculator.selectedBilling === 'monthly' ? item.plan.monthlyPrice * item.quantity : 
                   item.plan.yearlyPrice * item.quantity).toFixed(2)}
            </td>
          </tr>
        `;
      });
    }
    
    // Database Services
    const databasePlans = this.getSelectedPlansByServiceType('database');
    if (databasePlans.length > 0) {
      plansHtml += `
        <tr>
          <td colspan="6" style="padding: 12px; background-color: #e9d5ff; font-weight: bold; color: #581c87; border-bottom: 2px solid #c084fc; text-align: left;">
            <i class="bx bx-cylinder" style="margin-right: 8px;"></i> DATABASE SERVICES (${databasePlans.length} ${databasePlans.length === 1 ? 'Plan' : 'Plans'})
          </td>
        </tr>
        <tr style="background-color: #f9fafb; font-weight: bold;">
          <th style="padding: 12px; border-bottom: 2px solid #e5e7eb; text-align: left;">Plan Name</th>
          <th style="padding: 12px; border-bottom: 2px solid #e5e7eb; text-align: left;">Configuration</th>
          <th style="padding: 12px; border-bottom: 2px solid #e5e7eb; text-align: center;">Quantity</th>
          <th style="padding: 12px; border-bottom: 2px solid #e5e7eb; text-align: right;">Unit Price</th>
          <th style="padding: 12px; border-bottom: 2px solid #e5e7eb; text-align: right;" colspan="2">Total Price</th>
        </tr>
      `;
      databasePlans.forEach(item => {
        plansHtml += `
          <tr>
            <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">${item.plan.name}</td>
            <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">${item.plan.dbEngine}, ${item.plan.dbCpu} vCPU, ${item.plan.dbRam} RAM</td>
            <td style="padding: 10px; border-bottom: 1px solid #e5e7eb; text-align: center;">${item.quantity}</td>
            <td style="padding: 10px; border-bottom: 1px solid #e5e7eb; text-align: right;">
              INR ${(this.calculator.selectedBilling === 'hourly' ? item.plan.hourlyPrice : 
                   this.calculator.selectedBilling === 'monthly' ? item.plan.monthlyPrice : 
                   item.plan.yearlyPrice).toFixed(2)}
            </td>
            <td style="padding: 10px; border-bottom: 1px solid #e5e7eb; text-align: right; font-weight: bold;" colspan="2">
              INR ${(this.calculator.selectedBilling === 'hourly' ? item.plan.hourlyPrice * item.quantity : 
                   this.calculator.selectedBilling === 'monthly' ? item.plan.monthlyPrice * item.quantity : 
                   item.plan.yearlyPrice * item.quantity).toFixed(2)}
            </td>
          </tr>
        `;
      });
    }

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Cloud Pricing Estimate - Insightful IT Services</title>
        <style>
          @media print {
            body { margin: 0; font-size: 12px; }
            @page { margin: 1cm; size: A4; }
            .page-break { page-break-before: always; }
          }
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
            max-width: 210mm; /* A4 width */
            margin: 0 auto;
            background-color: white;
          }
          .header {
            text-align: center;
            margin-bottom: 20px;
            border-bottom: 3px solid #2563eb;
            padding-bottom: 15px;
          }
          .company-name {
            font-size: 24px;
            font-weight: bold;
            color: #1e40af;
            margin-bottom: 5px;
          }
          .document-title {
            font-size: 18px;
            color: #374151;
            margin-bottom: 10px;
          }
          .info-section {
            margin-bottom: 20px;
            padding: 10px;
            background-color: #f9fafb;
            border-radius: 6px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
            font-size: 12px;
          }
          th {
            background-color: #f3f4f6;
            padding: 10px;
            text-align: left;
            font-weight: bold;
            border-bottom: 2px solid #d1d5db;
          }
          td {
            padding: 8px 10px;
          }
          .summary {
            background-color: #eff6ff;
            padding: 15px;
            border-radius: 8px;
            margin-top: 20px;
            border: 1px solid #3b82f6;
          }
          .summary-row {
            display: flex;
            justify-content: space-between;
            padding: 6px 0;
            border-bottom: 1px solid #dbeafe;
            font-size: 14px;
          }
          .summary-row:last-child {
            border-bottom: none;
            font-weight: bold;
            font-size: 16px;
            color: #1e40af;
          }
          .footer {
            margin-top: 30px;
            text-align: center;
            color: #6b7280;
            font-size: 11px;
            border-top: 1px solid #e5e7eb;
            padding-top: 15px;
          }
          .billing-cycle {
            font-weight: bold;
            color: #374151;
            margin: 10px 0;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-name">Insightful IT Services LLP</div>
          <div class="document-title">Cloud Infrastructure Pricing Estimate</div>
          <div style="font-size: 14px; color: #6b7280; margin-top: 10px;">Date: ${currentDate}</div>
        </div>

        <div class="info-section">
          <div class="billing-cycle"><strong>Billing Cycle:</strong> ${this.calculator.selectedBilling.charAt(0).toUpperCase() + this.calculator.selectedBilling.slice(1)}</div>
          <div><strong>Total Plans:</strong> ${this.selectedPlans.length} ${this.selectedPlans.length === 1 ? 'Plan' : 'Plans'}</div>
        </div>

        <table>
          <tbody>
            ${plansHtml}
          </tbody>
        </table>

        <div class="summary">
          <div class="summary-row">
            <span>Estimated Hourly Cost:</span>
            <span>INR ${this.totalEstimateCost.toFixed(2)}/hr</span>
          </div>
          <div class="summary-row">
            <span>Estimated Monthly Cost:</span>
            <span>INR ${this.totalMonthlyCost.toFixed(2)}/mo</span>
          </div>
          <div class="summary-row">
            <span>Estimated Yearly Cost:</span>
            <span>INR ${this.totalYearlyCost.toFixed(2)}/yr</span>
          </div>
          <div class="summary-row">
            <span><strong>Total Minimum Billing:</strong></span>
            <span><strong>INR ${this.getTotalMinimumBilling().toFixed(2)}</strong></span>
          </div>
        </div>

        <div class="footer">
          <p><strong>Insightful IT Services LLP</strong></p>
          <p>610, Synergy Premises, Kanchpada, Malad (West), Mumbai - 400064</p>
          <p>Phone: +91 9167663638 | Email: info@insightfulitservices.com</p>
          <p style="margin-top: 10px;">This is an automated estimate. Final pricing may vary based on actual usage and additional services.</p>
        </div>
      </body>
      </html>
    `;
  }

  // Property to track email form visibility
  showEmailForm: boolean = false;
  
  // Property to store the recipient email address
  recipientEmail: string = '';
  
  // Method to open the email form
  openEmailForm(): void {
    if (this.selectedPlans.length === 0) {
      alert('Please add plans to your estimate before emailing.');
      return;
    }
    this.showEmailForm = true;
  }
  
  // Method to close the email form
  closeEmailForm(): void {
    this.showEmailForm = false;
    this.recipientEmail = '';
  }
  
  // Method to send the estimate via email
  emailEstimate(): void {
    if (!this.recipientEmail || !this.isValidEmail(this.recipientEmail)) {
      alert('Please enter a valid email address.');
      return;
    }
    
    // Create professional email body
    let emailBody = 'Cloud Infrastructure Pricing Estimate\n';
    emailBody += '='.repeat(50) + '\n\n';
    emailBody += `Date: ${new Date().toLocaleDateString('en-IN')}\n`;
    emailBody += `Billing Cycle: ${this.calculator.selectedBilling.charAt(0).toUpperCase() + this.calculator.selectedBilling.slice(1)}\n\n`;
    
    // Compute Services
    const computePlans = this.getSelectedPlansByServiceType('compute');
    if (computePlans.length > 0) {
      emailBody += '\nCOMPUTE SERVICES\n';
      emailBody += `Total Plans: ${computePlans.length}\n`;
      emailBody += '-'.repeat(50) + '\n';
      computePlans.forEach((item, index) => {
        const instanceType = this.getInstanceTypeFromPlanName(item.plan.name);
        const minBilling = item.plan.minimumBilling || 0;
        const totalMinBilling = minBilling * item.quantity;
        emailBody += `${index + 1}. ${item.plan.name}\n`;
        emailBody += `   Instance Type: ${instanceType}\n`;
        emailBody += `   Configuration: ${item.plan.vcpu} vCPU, ${item.plan.ram} RAM, ${item.plan.storage}\n`;
        emailBody += `   ${minBilling > 0 ? '** MINIMUM BILLING: INR ' + totalMinBilling + ' **' : 'Minimum Billing: Not Applicable'}\n`;
        emailBody += `   Quantity: ${item.quantity}\n`;
        emailBody += `   Unit Price: INR ${item.plan.monthlyPrice.toFixed(2)}/month\n`;
        emailBody += `   Total: INR ${(item.plan.monthlyPrice * item.quantity).toFixed(2)}\n\n`;
      });
    }
    
    // Storage Services
    const storagePlans = this.getSelectedPlansByServiceType('storage');
    if (storagePlans.length > 0) {
      emailBody += '\nSTORAGE SERVICES\n';
      emailBody += `Total Plans: ${storagePlans.length}\n`;
      emailBody += '-'.repeat(50) + '\n';
      storagePlans.forEach((item, index) => {
        emailBody += `${index + 1}. ${item.plan.name}\n`;
        emailBody += `   Configuration: ${item.plan.storage}, ${item.plan.diskType}, ${item.plan.iops} IOPS\n`;
        emailBody += `   Quantity: ${item.quantity}\n`;
        emailBody += `   Unit Price: INR ${item.plan.monthlyPrice.toFixed(2)}/month\n`;
        emailBody += `   Total: INR ${(item.plan.monthlyPrice * item.quantity).toFixed(2)}\n\n`;
      });
    }
    
    // Database Services
    const databasePlans = this.getSelectedPlansByServiceType('database');
    if (databasePlans.length > 0) {
      emailBody += '\nDATABASE SERVICES\n';
      emailBody += `Total Plans: ${databasePlans.length}\n`;
      emailBody += '-'.repeat(50) + '\n';
      databasePlans.forEach((item, index) => {
        emailBody += `${index + 1}. ${item.plan.name}\n`;
        emailBody += `   Configuration: ${item.plan.dbEngine}, ${item.plan.dbCpu} vCPU, ${item.plan.dbRam} RAM\n`;
        emailBody += `   Quantity: ${item.quantity}\n`;
        emailBody += `   Unit Price: INR ${item.plan.monthlyPrice.toFixed(2)}/month\n`;
        emailBody += `   Total: INR ${(item.plan.monthlyPrice * item.quantity).toFixed(2)}\n\n`;
      });
    }
    
    emailBody += '\nCOST SUMMARY\n';
    emailBody += '-'.repeat(50) + '\n';
    emailBody += `Estimated Hourly Cost: INR ${this.totalEstimateCost.toFixed(2)}/hr\n`;
    emailBody += `Estimated Monthly Cost: INR ${this.totalMonthlyCost.toFixed(2)}/mo\n`;
    emailBody += `Estimated Yearly Cost: INR ${this.totalYearlyCost.toFixed(2)}/yr\n\n`;
    
    emailBody += '\n' + '='.repeat(50) + '\n';
    emailBody += 'Insightful IT Services LLP\n';
    emailBody += '610, Synergy Premises, Kanchpada\n';
    emailBody += 'Malad (West), Mumbai - 400064\n';
    emailBody += 'Phone: +91 9167663638\n';
    emailBody += 'Email: info@insightfulitservices.com\n';
    emailBody += 'Website: https://insightfulitservices.com\n';
    
    // In a real application, you would send this via a backend service
    // For now, we'll show an alert to simulate the sending
    alert(`Estimate would be sent to: ${this.recipientEmail}

In a real application, this would send an email with the following details:

Subject: Cloud Infrastructure Pricing Estimate

Body:
${emailBody}`);
    
    // Close the form after sending
    this.closeEmailForm();
  }
  
  // Helper method to validate email format
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }
  
  // Property to track link form visibility
  showLinkForm: boolean = false;
  
  // Property to store the generated link
  generatedLink: string = '';
  
  // Property to store the shortened link
  shortenedLink: string = '';
  
  // Method to open the link form
  openLinkForm(): void {
    if (this.selectedPlans.length === 0) {
      alert('Please add plans to your estimate before copying the link.');
      return;
    }
    
    // Generate the full URL
    this.generatedLink = this.generateFullLink();
    
    // Generate a shortened version (simulated)
    this.shortenedLink = this.generateShortenedLink(this.generatedLink);
    
    // Show the form
    this.showLinkForm = true;
  }
  
  // Method to generate the full link
  private generateFullLink(): string {
    // Build the query parameters from current calculator settings
    const queryParams = new URLSearchParams();
    queryParams.set('serviceType', this.calculator.selectedServiceType);
    queryParams.set('instanceType', this.calculator.selectedInstanceType);
    queryParams.set('os', this.calculator.selectedOS);
    queryParams.set('billing', this.calculator.selectedBilling);
    
    // Serialize selected plans
    const plansData = this.selectedPlans.map(item => {
      return `${item.plan.name}:${item.quantity}`;
    }).join('|');
    
    queryParams.set('plans', plansData);
    
    // Generate the full URL with parameters
    const currentUrl = window.location.origin + window.location.pathname;
    return `${currentUrl}?${queryParams.toString()}`;
  }
  
  // Method to generate a shortened link using the URL shortener service
  private generateShortenedLink(fullLink: string): string {
    // Use the URL shortener service to create a real shortened link
    return this.urlShortenerService.shortenUrl(fullLink);
  }
  
  // Method to copy the link to clipboard
  copyLinkToClipboard(linkType: 'full' | 'shortened'): void {
    const linkToCopy = linkType === 'full' ? this.generatedLink : this.shortenedLink;
    
    navigator.clipboard.writeText(linkToCopy).then(() => {
      alert(`${linkType === 'full' ? 'Full' : 'Shortened'} link copied to clipboard!`);
    }).catch(err => {
      console.error('Failed to copy link: ', err);
      // Fallback: show the link in an alert
      alert(`Failed to copy link automatically. Here is your link:\n\n${linkToCopy}`);
    });
  }
  
  // Method to close the link form
  closeLinkForm(): void {
    this.showLinkForm = false;
    this.generatedLink = '';
    this.shortenedLink = '';
  }
  
  // Method to generate and copy the shareable link (maintaining backward compatibility)
  copyEstimateLink(): void {
    this.openLinkForm();
  }
  
  // Method to load estimate from URL parameters
  private loadEstimateFromUrl(): void {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Load calculator settings
    const serviceType = urlParams.get('serviceType') as 'compute' | 'storage' | 'database';
    const instanceType = urlParams.get('instanceType') as 'Shared CPU' | 'Dedicated CPU' | 'High Memory';
    const os = urlParams.get('os') as 'linux' | 'windows';
    const billing = urlParams.get('billing') as 'hourly' | 'monthly' | 'yearly';
    
    if (serviceType) this.calculator.selectedServiceType = serviceType;
    if (instanceType) this.calculator.selectedInstanceType = instanceType;
    if (os) this.calculator.selectedOS = os;
    if (billing) this.calculator.selectedBilling = billing;
    
    // Load selected plans
    const plansParam = urlParams.get('plans');
    if (plansParam) {
      const planEntries = plansParam.split('|');
      
      for (const entry of planEntries) {
        const [planName, quantityStr] = entry.split(':');
        const quantity = parseInt(quantityStr) || 1;
        
        // Find the plan in all possible arrays
        const allPlans = [
          ...this.sharedCpuPlansLinux, ...this.sharedCpuPlansWindows,
          ...this.dedicatedCpuPlansLinux, ...this.dedicatedCpuPlansWindows,
          ...this.highMemoryPlansLinux, ...this.highMemoryPlansWindows,
          ...this.storagePlans, ...this.databasePlans
        ];
        
        const plan = allPlans.find(p => p.name === planName);
        
        if (plan) {
          // Add the plan to selectedPlans
          this.selectedPlans.push({
            plan: plan,
            quantity: quantity
          });
        }
      }
      
      // Update total calculation after loading plans
      this.calculateTotalEstimate();
    }
  }

  // Method to add multiple plans to the estimate
  selectedPlans: { plan: PricingPlan, quantity: number }[] = [];

  addPlanToEstimate(): void {
    // Make sure plan details are updated before adding
    this.updatePlanDetails();
    
    if (this.selectedPlanDetails) {
      // Check if plan already exists in the estimate
      const existingPlanIndex = this.selectedPlans.findIndex(item => item.plan.name === this.selectedPlanDetails?.name);
      
      // Use the quantity from the selected plan details if available, otherwise use calculator quantity
      const planQuantity = this.selectedPlanDetails.quantity || this.calculator.quantity;
      
      if (existingPlanIndex > -1) {
        // Add to existing quantity if plan already exists
        this.selectedPlans[existingPlanIndex].quantity += planQuantity;
      } else {
        // Add new plan to the estimate
        this.selectedPlans.push({
          plan: this.selectedPlanDetails,
          quantity: planQuantity
        });
      }
      
      // Update the total estimate
      this.calculateTotalEstimate();
      this.cdr.detectChanges();
    }
  }

  removePlanFromEstimate(planName: string): void {
    this.selectedPlans = this.selectedPlans.filter(item => item.plan.name !== planName);
    this.calculateTotalEstimate();
    this.cdr.detectChanges();
  }

  totalEstimateCost: number = 0;
  totalMonthlyCost: number = 0;
  totalYearlyCost: number = 0;

  private calculateTotalEstimate(): void {
    let totalHourly = 0;
    let totalMonthly = 0;
    let totalYearly = 0;

    for (const item of this.selectedPlans) {
      switch (this.calculator.selectedBilling) {
        case 'hourly':
          totalHourly += item.plan.hourlyPrice * item.quantity;
          totalMonthly += (item.plan.hourlyPrice * 24 * 30) * item.quantity;
          totalYearly += (item.plan.hourlyPrice * 24 * 30 * 12) * item.quantity;
          break;
        case 'monthly':
          totalHourly += (item.plan.monthlyPrice / (24 * 30)) * item.quantity;
          totalMonthly += item.plan.monthlyPrice * item.quantity;
          totalYearly += item.plan.monthlyPrice * 12 * item.quantity;
          break;
        case 'yearly':
          totalHourly += (item.plan.yearlyPrice / (24 * 30 * 12)) * item.quantity;
          totalMonthly += (item.plan.yearlyPrice / 12) * item.quantity;
          totalYearly += item.plan.yearlyPrice * item.quantity;
          break;
      }
    }

    this.totalEstimateCost = parseFloat(totalHourly.toFixed(2));
    this.totalMonthlyCost = parseFloat(totalMonthly.toFixed(2));
    this.totalYearlyCost = parseFloat(totalYearly.toFixed(2));
    this.cdr.detectChanges();
  }
  
  // Calculate total minimum billing across all selected plans considering quantities
  getTotalMinimumBilling(): number {
    return this.selectedPlans.reduce((total, item) => {
      const minBilling = item.plan.minimumBilling || 0;
      return total + (minBilling * item.quantity);
    }, 0);
  }

  // Get all available plans based on current selections
  getAvailablePlans(): PricingPlan[] {
    if (this.calculator.selectedServiceType === 'compute') {
      if (this.calculator.selectedInstanceType === 'Shared CPU') {
        return this.calculator.selectedOS === 'linux' ? this.sharedCpuPlansLinux : this.sharedCpuPlansWindows;
      } else if (this.calculator.selectedInstanceType === 'Dedicated CPU') {
        return this.calculator.selectedOS === 'linux' ? this.dedicatedCpuPlansLinux : this.dedicatedCpuPlansWindows;
      } else if (this.calculator.selectedInstanceType === 'High Memory') {
        return this.calculator.selectedOS === 'linux' ? this.highMemoryPlansLinux : this.highMemoryPlansWindows;
      }
    } else if (this.calculator.selectedServiceType === 'storage') {
      return this.storagePlans;
    } else if (this.calculator.selectedServiceType === 'database') {
      // Filter database plans by instance type
      return this.databasePlans.filter(plan => plan.name.includes(this.calculator.selectedInstanceType));
    }
    return [];
  }

  // Filtered view for Available Plans based on search text
  getFilteredAvailablePlans(): PricingPlan[] {
    const filtersActive = (this.filterVcpuExact != null) || (this.filterRamExactGB != null);
    const plans = (this.calculator.selectedServiceType === 'compute' && filtersActive)
      ? this.getAllComputePlans()
      : this.getAvailablePlans();
    const q = (this.searchQuery || '').trim().toLowerCase();
    let filtered = plans;
    if (this.filterVcpuExact != null) {
      const target = this.filterVcpuExact as number;
      filtered = filtered.filter(p => {
        const cpu = (p.vcpu != null) ? p.vcpu : (p.dbCpu != null ? p.dbCpu : null);
        return cpu != null && cpu === target;
      });
    }
    if (this.filterRamExactGB != null) {
      const targetRam = this.filterRamExactGB as number;
      filtered = filtered.filter(p => {
        const ramGb = (p.ram ? this.parseRamGB(p.ram) : (p.dbRam ? this.parseRamGB(p.dbRam) : 0));
        return ramGb !== 0 && ramGb === targetRam;
      });
    }
    if (!q) return filtered;
    return filtered.filter(p => {
      const fields = [
        p.name,
        p.storage,
        p.diskType,
        p.throughput,
        p.iops?.toString(),
        p.ram,
        p.vcpu?.toString(),
        p.dbEngine,
        p.dbSize,
        p.dbRam,
        p.dbCpu?.toString()
      ].filter(Boolean).map(s => String(s).toLowerCase());
      return fields.some(f => f.includes(q));
    });
  }

  getPlanBadge(plan: PricingPlan): 'best' | 'popular' | null {
    const list = this.getFilteredAvailablePlans();
    if (!list.length) return null;
    const scores = list.map(p => ({ p, score: this.computeValueScore(p) }))
      .sort((a, b) => a.score - b.score);
    const best = scores[0]?.p;
    const popular = scores[1]?.p;
    if (best && plan.name === best.name) return 'best';
    if (popular && plan.name === popular.name) return 'popular';
    return null;
  }

  private computeValueScore(p: PricingPlan): number {
    const capacityUnits = (this.parseRamGB(p.ram) / 4) + (p.vcpu || 0);
    const price = this.calculator.selectedBilling === 'hourly'
      ? p.hourlyPrice
      : this.calculator.selectedBilling === 'yearly'
        ? (p.yearlyPrice / 12)
        : p.monthlyPrice;
    const denom = capacityUnits || 1;
    return price / denom;
  }

  private parseRamGB(ram?: string): number {
    if (!ram) return 0;
    const match = ram.match(/(\d+)\s*GB/i);
    return match ? parseInt(match[1], 10) : 0;
  }

  getAnnualSavings(plan: PricingPlan): { amount: number, percent: number } {
    const monthly12 = (plan.monthlyPrice || 0) * 12;
    const yearly = plan.yearlyPrice || 0;
    const amount = Math.max(0, monthly12 - yearly);
    const percent = monthly12 > 0 ? (amount / monthly12) * 100 : 0;
    return {
      amount: parseFloat(amount.toFixed(2)),
      percent: parseFloat(percent.toFixed(1))
    };
  }

  isInCompare(plan: PricingPlan): boolean {
    return this.compareSelection.some(p => p.name === plan.name);
  }

  toggleCompare(plan: PricingPlan): void {
    if (this.isInCompare(plan)) {
      this.compareSelection = this.compareSelection.filter(p => p.name !== plan.name);
    } else {
      if (this.compareSelection.length >= 3) {
        this.compareSelection = [...this.compareSelection.slice(1), plan];
      } else {
        this.compareSelection = [...this.compareSelection, plan];
      }
    }
    this.cdr.detectChanges();
  }

  openCompare(): void {
    if (this.compareSelection.length >= 2) {
      this.showCompare = true;
      this.cdr.detectChanges();
    }
  }

  closeCompare(): void {
    this.showCompare = false;
    this.cdr.detectChanges();
  }

  getPlanConfigString(plan: PricingPlan): string {
    if (plan.serviceType === 'compute') {
      return `${plan.vcpu || '-'} vCPU, ${plan.ram || '-'} RAM, ${plan.storage || '-'}`;
    }
    if (plan.serviceType === 'storage') {
      return `${plan.storage || '-'}, ${plan.diskType || '-'}, ${plan.iops || '-'} IOPS`;
    }
    if (plan.serviceType === 'database') {
      return `${plan.dbEngine || '-'}, ${plan.dbCpu || '-'} vCPU, ${plan.dbRam || '-'}`;
    }
    return '';
  }
  
  // Methods to check if certain storage types are available
  hasBlockStoragePlans(): boolean {
    return this.getAvailablePlans().some(p => p.name.includes('Block Storage'));
  }
  
  hasObjectStoragePlans(): boolean {
    return this.getAvailablePlans().some(p => p.name.includes('Object Storage'));
  }
  
  hasArchiveStoragePlans(): boolean {
    return this.getAvailablePlans().some(p => p.name.includes('Archive Storage'));
  }
  
  // Get selected plans filtered by service type
  getSelectedPlansByServiceType(serviceType: 'compute' | 'storage' | 'database'): { plan: PricingPlan, quantity: number }[] {
    return this.selectedPlans.filter(item => item.plan.serviceType === serviceType);
  }

  // Helper: all compute plans across OS and instance types
  private getAllComputePlans(): PricingPlan[] {
    return [
      ...this.sharedCpuPlansLinux, ...this.sharedCpuPlansWindows,
      ...this.dedicatedCpuPlansLinux, ...this.dedicatedCpuPlansWindows,
      ...this.highMemoryPlansLinux, ...this.highMemoryPlansWindows,
    ];
  }
  
  // Helper method to determine instance type from plan name
  private getInstanceTypeFromPlanName(planName: string): string {
    if (planName.includes('Power')) return 'Dedicated CPU';
    if (planName.includes('RAM')) return 'High Memory';
    return 'Shared CPU';
  }
  
  // Helper method to get minimum billing amount
  private getMinimumBilling(planName: string): number {
    // Find the plan in all possible arrays to get its minimumBilling value
    const allPlans = [
      ...this.sharedCpuPlansLinux, ...this.sharedCpuPlansWindows,
      ...this.dedicatedCpuPlansLinux, ...this.dedicatedCpuPlansWindows,
      ...this.highMemoryPlansLinux, ...this.highMemoryPlansWindows,
      ...this.storagePlans, ...this.databasePlans
    ];
    
    const plan = allPlans.find(p => p.name === planName);
    return plan?.minimumBilling || 0; // Return 0 if no minimum billing is set
  }
}
